import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthUserContext } from '../../../components/Session';
import TemplateCard from '../components/TemplateCard';
import TemplateFilters from '../components/TemplateFilters';
import CreateTemplateModal from '../components/CreateTemplateModal';
import GenerateWizardModal from '../components/GenerateWizardModal';
import starterTemplates from '../data/starterTemplates';
import { templatesList, templatesCreate, templatesDelete } from '../utils/templatesApi';
import '../templates.css';

const TABS = [
    { key: 'AGREEMENTS', label: 'Agreements' },
    { key: 'LETTERS_NOTICES', label: 'Letters / Notices' },
    { key: 'MARKETING', label: 'Marketing' },
];

const SUBCATEGORIES = [
    'All',
    'Sale',
    'Rental',
    'Brokerage',
    'Land',
    'Construction',
    'Commercial',
    'Compliance',
];

const JURISDICTIONS = [
    'All India',
    'Telangana',
    'Karnataka',
    'Maharashtra',
    'Tamil Nadu',
    'Andhra Pradesh',
];

const TemplatesPage = ({ firebase }) => {
    const authUser = useContext(AuthUserContext);
    const history = useHistory();

    const [activeTab, setActiveTab] = useState('AGREEMENTS');
    const [subcategory, setSubcategory] = useState('All');
    const [jurisdiction, setJurisdiction] = useState('All India');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('recent');
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [generateTemplate, setGenerateTemplate] = useState(null);
    const [resetting, setResetting] = useState(false);

    const resolveCategoryTab = (template) => {
        if (template?.categoryTab) return template.categoryTab;
        const category = (template?.category || '').toLowerCase();
        if (['letters', 'notices', 'letters / notices', 'letters_notices'].includes(category)) {
            return 'LETTERS_NOTICES';
        }
        if (['marketing', 'promo', 'branding'].includes(category)) {
            return 'MARKETING';
        }
        return 'AGREEMENTS';
    };

    const normalizeTemplate = (template) => ({
        ...template,
        categoryTab: resolveCategoryTab(template),
        subcategory: template?.subcategory || template?.category || 'All',
    });

    // Generate unique ID
    const generateId = () => `tpl-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Seed starter templates if none exist
    const seedTemplates = useCallback(async () => {
        if (!firebase || !authUser?.uid) return;

        try {
            const promises = starterTemplates.map((template) => {
                const id = generateId();
                return templatesCreate(firebase, id, {
                    ...template,
                    categoryTab: 'AGREEMENTS', // All agreement templates
                    subcategory: template.category,
                    version: 1,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                });
            });
            await Promise.all(promises);
        } catch (err) {
            console.error('Failed to seed templates:', err);
        }
    }, [firebase, authUser?.uid]);

    // Load templates
    const loadTemplates = useCallback(async () => {
        if (!firebase || !authUser?.uid) return;

        setLoading(true);
        try {
            const response = await templatesList(firebase);
            const items = response?.items || [];

            // If no templates exist, seed starter templates
            if (items.length === 0) {
                await seedTemplates();
                const seededResponse = await templatesList(firebase);
                const seededItems = (seededResponse?.items || []).map(normalizeTemplate);
                setTemplates(seededItems);
            } else {
                setTemplates(items.map(normalizeTemplate));
            }
        } catch (err) {
            console.error('Failed to load templates:', err);
            // If backend fails, show starter templates locally
            setTemplates(
                starterTemplates.map((t, i) =>
                    normalizeTemplate({ ...t, id: `local-${i}` }),
                ),
            );
        } finally {
            setLoading(false);
        }
    }, [firebase, authUser?.uid, seedTemplates]);

    // Reset templates to professional defaults
    const handleResetTemplates = async () => {
        if (!firebase || !authUser?.uid) return;
        if (!window.confirm('This will delete ALL existing templates and replace them with the new professional templates. Continue?')) {
            return;
        }

        setResetting(true);
        try {
            // Delete all existing templates
            const deletePromises = templates.map((t) => templatesDelete(firebase, t.id));
            await Promise.all(deletePromises);

            // Seed new professional templates
            await seedTemplates();

            // Reload
            await loadTemplates();
            alert('Templates have been reset to professional defaults!');
        } catch (err) {
            console.error('Failed to reset templates:', err);
            alert('Failed to reset templates. Please try again.');
        } finally {
            setResetting(false);
        }
    };

    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    // Filter and sort templates
    const filteredTemplates = useMemo(() => {
        let result = templates.filter((t) => t.categoryTab === activeTab);

        if (subcategory !== 'All') {
            result = result.filter((t) => t.subcategory === subcategory);
        }

        if (jurisdiction !== 'All India') {
            result = result.filter((t) => t.jurisdiction === jurisdiction || t.jurisdiction === 'India');
        }

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            result = result.filter(
                (t) =>
                    t.title?.toLowerCase().includes(query) ||
                    t.tags?.some((tag) => tag.toLowerCase().includes(query))
            );
        }

        // Sort
        if (sortBy === 'recent') {
            result.sort((a, b) => {
                const dateA = new Date(a.updatedAt || 0).getTime();
                const dateB = new Date(b.updatedAt || 0).getTime();
                return dateB - dateA;
            });
        } else if (sortBy === 'alpha') {
            result.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        }

        return result;
    }, [templates, activeTab, subcategory, jurisdiction, searchQuery, sortBy]);

    const handleEdit = (template) => {
        history.push(`/franchise-dashboard/templates/${template.id}`);
    };

    const handleUse = (template) => {
        setGenerateTemplate(template);
    };

    const handleCreateTemplate = async (type, data) => {
        if (!firebase) return;

        const id = generateId();
        let newTemplate = {
            title: 'Untitled Template',
            category: 'Agreements',
            categoryTab: 'AGREEMENTS',
            subcategory: 'Sale',
            tags: [],
            jurisdiction: 'India',
            status: 'draft',
            blocks: [{ id: 'b1', type: 'heading', text: 'New Template' }],
            variables: [],
            version: 1,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };

        if (type === 'blank') {
            // Use default
        } else if (type === 'duplicate' && data?.sourceTemplate) {
            newTemplate = {
                ...data.sourceTemplate,
                id: undefined,
                title: `${data.sourceTemplate.title} (Copy)`,
                status: 'draft',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
        } else if (type === 'import' && data?.text) {
            newTemplate.blocks = [
                { id: 'b1', type: 'heading', text: 'Imported Template' },
                { id: 'b2', type: 'paragraph', text: data.text },
            ];
        }

        try {
            await templatesCreate(firebase, id, newTemplate);
            setShowCreateModal(false);
            history.push(`/franchise-dashboard/templates/${id}`);
        } catch (err) {
            console.error('Failed to create template:', err);
        }
    };

    return (
        <div className="templates-page">
            <div className="templates-header">
                <h1>Templates</h1>
                <p>Create and manage professional documents for your real estate business.</p>
            </div>

            {/* Legal Disclaimer */}
            <div className="templates-disclaimer">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                </svg>
                <span>
                    <strong>Disclaimer:</strong> These templates are starting points for documentation.
                    Please consult a legal professional before using any documents for official purposes.
                </span>
            </div>

            {/* Tabs */}
            <div className="templates-tabs">
                {TABS.map((tab) => (
                    <button
                        key={tab.key}
                        className={`templates-tab ${activeTab === tab.key ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
                <button
                    className="templates-tab"
                    onClick={() => history.push('/franchise-dashboard/templates/documents')}
                >
                    Generated Docs
                </button>
            </div>

            <div className="templates-content">
                {/* Sidebar Filters */}
                <TemplateFilters
                    subcategories={SUBCATEGORIES}
                    activeSubcategory={subcategory}
                    onSubcategoryChange={setSubcategory}
                    jurisdictions={JURISDICTIONS}
                    activeJurisdiction={jurisdiction}
                    onJurisdictionChange={setJurisdiction}
                />

                {/* Main Content */}
                <div className="templates-main">
                    {/* Controls */}
                    <div className="templates-controls">
                        <div className="templates-search">
                            <input
                                type="text"
                                placeholder="Search templates..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <div className="templates-sort">
                            <label>Sort:</label>
                            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                                <option value="recent">Recently Updated</option>
                                <option value="alpha">Alphabetical</option>
                            </select>
                        </div>

                        <button
                            className="template-btn secondary"
                            style={{ padding: '10px 16px', fontSize: '13px' }}
                            onClick={handleResetTemplates}
                            disabled={resetting}
                        >
                            {resetting ? 'Resetting...' : '↻ Reset to Defaults'}
                        </button>

                        <button className="btn-create-template" onClick={() => setShowCreateModal(true)}>
                            + Create Template
                        </button>
                    </div>

                    {/* Grid */}
                    {loading ? (
                        <div className="templates-grid">
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <div key={i} className="template-skeleton">
                                    <div className="skeleton-line short" />
                                    <div className="skeleton-line medium" />
                                    <div className="skeleton-line short" />
                                </div>
                            ))}
                        </div>
                    ) : filteredTemplates.length === 0 ? (
                        <div className="templates-empty">
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                            </svg>
                            <h3>No templates found</h3>
                            <p>Try adjusting your filters or create a new template.</p>
                        </div>
                    ) : (
                        <div className="templates-grid">
                            {filteredTemplates.map((template) => (
                                <TemplateCard
                                    key={template.id}
                                    template={template}
                                    onEdit={() => handleEdit(template)}
                                    onUse={() => handleUse(template)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <CreateTemplateModal
                    templates={templates}
                    onClose={() => setShowCreateModal(false)}
                    onCreate={handleCreateTemplate}
                />
            )}

            {/* Generate Wizard Modal */}
            {generateTemplate && (
                <GenerateWizardModal
                    firebase={firebase}
                    template={generateTemplate}
                    onClose={() => setGenerateTemplate(null)}
                />
            )}
        </div>
    );
};

export default TemplatesPage;
